import { useEffect } from "react"
import { useAtom, useSetAtom } from "jotai"
import { HiSparkles } from "react-icons/hi2"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { Drawer, DrawerContent, DrawerTitle } from "@mylaunch/components/ui"
import type { Plan } from "@apps/gwservice/types"
import { plansDrawerOpenAtom } from "../atoms/plans-drawer"
import { plansQueryKey, usePlans } from "../hooks/use-plans"
import { resolvePending } from "../server/plans.functions"
import { ExpertDmPlanCard } from "./expert-dm-plan-card"
import { PlanCard } from "./plan-card"
import { SettingsItem } from "~/components/settings-item"

const fmt = (s: string) =>
  new Date(s).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
  })

export function PlansTrigger({ children }: { children: React.ReactNode }) {
  const setOpen = useSetAtom(plansDrawerOpenAtom)
  return (
    <div className="w-full" onClick={() => setOpen("default")}>
      {children}
    </div>
  )
}

export function PlansTriggerLabel({ className }: { className?: string }) {
  const { data } = usePlans()
  const plans = data.plans
  const currentPlan = plans.find((p) => !!p.chargeDate)
  const isUnsubscribed = currentPlan && !currentPlan.chargeAmount
  const label = currentPlan ? `Тариф (${currentPlan.name.toLowerCase()})` : "Выбрать тариф"
  const right = <span className="text-sm text-muted-foreground">{triggerHint(isUnsubscribed, plans, currentPlan)}</span>

  return <SettingsItem className={className} text={label} icon={<HiSparkles />} right={right} />
}

function triggerHint(isUnsubscribed: boolean | undefined, plans: Plan[], currentPlan?: Plan) {
  if (!currentPlan) {
    const minPrice = plans.reduce((min, plan) => Math.min(min, plan.minPrice), Number.POSITIVE_INFINITY)
    return `от ${Number.isFinite(minPrice) ? minPrice : 0}₽ / мес.`
  }
  if (isUnsubscribed && currentPlan.chargeDate) return `до ${fmt(currentPlan.chargeDate)}`
  return currentPlan.chargeDate ? `продление ${fmt(currentPlan.chargeDate)}` : ""
}

export function PlansDrawer() {
  const [source, setSource] = useAtom(plansDrawerOpenAtom)
  const { data } = usePlans()
  const plans = data.plans
  const queryClient = useQueryClient()
  const search = useSearch({ strict: false })
  const navigate = useNavigate()
  const isPayReturn = search.pay === "return"

  const hasPendingPayment = plans.some((plan) => plan.pendingPayment)
  const hasPendingMethod = !!data.user?.payment?.pendingMethodId
  const hasPending = hasPendingPayment || hasPendingMethod
  const isOpen = source !== null
  const handleOpenChange = (next: boolean) => setSource(next ? source ?? "default" : null)

  const { mutate: resolve } = useMutation({
    mutationFn: () => resolvePending(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: plansQueryKey }),
  })

  useEffect(() => {
    if (hasPending) resolve()
  }, [hasPending, resolve])

  useEffect(() => {
    if (isPayReturn) setSource("default")
  }, [isPayReturn, setSource])

  useEffect(() => {
    if (!isPayReturn) return
    if (hasPending) return
    void navigate({ to: "/", search: { tab: "profile" }, replace: true })
  }, [isPayReturn, hasPending, navigate])

  const expertDmPlan = source === "expertDm" ? plans.find((plan) => plan.rank === 1) : undefined

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerContent handle={false} className="bg-white border-0 shadow-none">
        <DrawerTitle className="sr-only">Тарифы</DrawerTitle>
        {expertDmPlan ? (
          <div className="overflow-y-auto py-m">
            <ExpertDmPlanCard plan={expertDmPlan} />
          </div>
        ) : (
          <div className="overflow-y-auto py-m flex flex-col gap-l">
            {plans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} hasPendingMethod={hasPendingMethod} />
            ))}
          </div>
        )}
      </DrawerContent>
    </Drawer>
  )
}
